import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { createConsultingSession } from '../utils/sessionService';
import { calculateResumeProgress, updateSessionProgressFromResumeData } from '../utils/progressSync';

const ConsultingContext = createContext();

export const useConsulting = () => {
  const context = useContext(ConsultingContext);
  if (!context) {
    throw new Error('useConsulting must be used within a ConsultingProvider');
  }
  return context;
};

export const ConsultingProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [extractedResumeData, setExtractedResumeData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    github: '',
    title: '',
    summary: '',
    keyStrengths: [],
    experiences: [],
    education: [],
    skills: [],
    certificates: [],
  });
  const [currentlyUpdatingField, setCurrentlyUpdatingField] = useState(null);
  const [loading, setLoading] = useState(false);

  // 사용자가 로그인했을 때 활성 세션 로드
  useEffect(() => {
    if (user) {
      loadActiveSession();
    } else {
      // 로그아웃 시 상태 초기화
      setCurrentSession(null);
      setMessages([]);
      setExtractedResumeData({
        name: '',
        email: '',
        phone: '',
        address: '',
        github: '',
        title: '',
        summary: '',
        keyStrengths: [],
        experiences: [],
        education: [],
        skills: [],
        certificates: [],
      });
    }
  }, [user]);

  // 사용자 프로필 확인 및 생성 함수
  const ensureUserProfile = async (user, userName = '') => {
    try {

      
      // 기존 프로필 확인
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }
      
      if (existingProfile) {
        return existingProfile;
      }
      
      // 프로필이 없으면 생성
      
      const newProfile = {
        id: user.id,
        email: user.email,
        name: userName || user.user_metadata?.full_name || user.user_metadata?.name || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data: createdProfile, error: createError } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single();
      
      if (createError) {
        throw createError;
      }
      
      return createdProfile;
      
    } catch (error) {
      throw error;
    }
  };

  // 활성 세션 로드
  const loadActiveSession = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // 활성 상태의 세션 찾기 (삭제되지 않은 것만)
      const { data: sessions, error: sessionError } = await supabase
        .from('consulting_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .is('deleted_at', null)  // 삭제되지 않은 세션만 조회
        .order('created_at', { ascending: false })
        .limit(1);

      if (sessionError) {
        return;
      }

      if (sessions && sessions.length > 0) {
        const session = sessions[0];
        setCurrentSession(session);

        // 세션의 메시지들 로드
        await loadSessionMessages(session.id);
        
        // 세션의 이력서 데이터 로드
        await loadSessionResumeData(session.id);
      }
    } catch (error) {
      // Error in loading active session
    } finally {
      setLoading(false);
    }
  };

  // 세션 메시지 로드
  const loadSessionMessages = async (sessionId) => {
    try {
      const { data: chatMessages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) {
        return;
      }

      setMessages(chatMessages.map(msg => ({
        id: msg.id,
        sender: msg.sender,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        isTyping: msg.is_typing
      })));
    } catch (error) {
      // Error in loading session messages
    }
  };

  // 세션 이력서 데이터 로드
  const loadSessionResumeData = async (sessionId) => {
    try {
      const { data: resumes, error } = await supabase
        .from('resumes')
        .select('*')
        .eq('session_id', sessionId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        return;
      }

      if (resumes && resumes.length > 0) {
        const resume = resumes[0];
        
        setExtractedResumeData({
          name: resume.name || '',
          email: resume.email || '',
          phone: resume.phone || '',
          address: resume.address || '',
          github: resume.github || '',
          title: resume.title || '',
          summary: resume.summary || '',
          keyStrengths: resume.key_strengths || [],
          experiences: resume.experiences || [],
          education: resume.education || [],
          skills: resume.skills || [],
          certificates: resume.certificates || [],
        });
        setCurrentlyUpdatingField(resume.currently_updating_field);
      }
    } catch (error) {
      // Error in loading session resume data
    }
  };

  // 새 세션 시작 (consultant 객체 파라미터 추가)
  const startNewSession = async (userName = '', consultant = null) => {
    // consultant 객체가 없으면 기본값 사용
    const systemPromptId = consultant?.systemPromptId || 10;
    const consultantType = consultant?.consultantType || 'latte-manager';
    
    // console.log('🚀 [startNewSession] 새 세션 시작', {
    //   systemPromptId,
    //   consultantType,
    //   userName,
    //   timestamp: new Date().toISOString()
    // });
    
    if (!user) {
      return;
    }

    try {
      setLoading(true);

      // 🔥 중요: 기존 상태 완전 초기화 (세션 격리의 햕심)
      setMessages([]);
      setExtractedResumeData({
        name: '',
        email: '',
        phone: '',
        address: '',
        github: '',
        title: '',
        summary: '',
        keyStrengths: [],
        experiences: [],
        education: [],
        skills: [],
        certificates: [],
      });
      setCurrentSession(null);
      setCurrentlyUpdatingField(null);
      
      // Gemini 채팅 세션도 리셋 (기존 세션이 있다면)
      const { resetChatSession } = await import('../utils/geminiAPI_SDK');
      // startNewSession에서는 currentSession의 ID만 사용 가능
      if (currentSession?.id) {
        resetChatSession(currentSession.id);
      }

      // 0. 사용자 프로필 확인 및 생성 (필요시)
      await ensureUserProfile(user, userName);

      // 1. 먼저 기존 활성 세션들을 확인
      const { data: existingSessions, error: checkError } = await supabase
        .from('consulting_sessions')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .is('deleted_at', null);  // 삭제되지 않은 세션만 확인

      if (checkError) {
        throw checkError;
      }

      // 2. 기존 활성 세션들을 완료 상태로 변경 (있다면)
      if (existingSessions && existingSessions.length > 0) {
        const { error: updateError } = await supabase
          .from('consulting_sessions')
          .update({ 
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (updateError) {
          throw updateError;
        }
      }

      // 3. 잠시 대기 (동시성 문제 방지)
      await new Promise(resolve => setTimeout(resolve, 100));

      // 4. 새 세션 생성 (프롬프트 버전 포함)
      const sessionName = userName ? `${userName}_${new Date().toISOString().slice(0, 10)}` : `세션_${new Date().toISOString().slice(0, 10)}`;
      
      const session = await createConsultingSession(user.id, sessionName, systemPromptId, consultantType);
      
      // console.log('✅ [startNewSession] DB 세션 생성 완료', {
      //   sessionId: session.id,
      //   systemPromptId: session.system_prompt_id,
      //   consultantType: session.consultant_type,
      //   status: session.status
      // });
      
      setCurrentSession(session);
      
      // 새 세션을 위한 Gemini 채팅 세션 초기화 (명시적 프롬프트 ID와 세션 ID 사용)
      if (systemPromptId && session.id) {
        // console.log('🤖 [startNewSession] Gemini 세션 초기화 시작', {
        //   sessionId: session.id,
        //   systemPromptId
        // });
        
        const { initializeNewSessionWithPrompt } = await import('../utils/geminiAPI_SDK');
        await initializeNewSessionWithPrompt(extractedResumeData, systemPromptId, session.id);
        
        // console.log('✅ [startNewSession] Gemini 세션 초기화 완료');
      }

      // 컨설턴트 타입에 따른 초기 AI 메시지 생성
      let firstAiMessages;
      
      if (consultantType === 'latte-boss') {
        // 라떼부장님 스타일
        firstAiMessages = userName
          ? [
              `어서와! 나는 경력 30년차 라떼부장이야. ${userName}, 라떼는 이력서 한 장에 인생이 완전 바뀌기도 했어. 그러니 우리 함께 이력서 제대로 만들어보자고!`,
              `자네 어떤 회사나 직무를 노리고 있는가? 아니면 일단 경력 정리부터 하고 싶은가?`
            ]
          : [
              `이 친구야! 나는 경력 30년차 라떼부장이라고 해. 같이 이력서 한 번 제대로 만들어보자고!`,
              `먼저 이름부터 말해봐. 내가 호칭을 정해줄게.`
            ];
      } else {
        // 친절한 매니저 스타일 (기본)
        firstAiMessages = userName
          ? [
              `안녕하세요 ${userName}님! 라떼매니저 입니다. 말씀하신 목적에 맞춰 최고의 이력서를 만들 수 있도록 돕겠습니다.`,
              `먼저, 어떤 회사나 포지션을 생각하고 계신가요? 혹은 전반적인 경력 정리가 목표이신가요?`
            ]
          : [
              '안녕하세요! 저는 여러분의 이력서 작성을 도와드릴 라떼매니저 입니다.',
              '먼저 성함을 알려주시겠어요?'
            ];
      }

      // 초기 메시지들을 각각 저장
      for (const messageContent of firstAiMessages) {
        const { error: messageError } = await supabase
          .from('chat_messages')
          .insert([{
            session_id: session.id,
            sender: 'ai',
            content: messageContent,
            message_type: 'text'
          }]);

        if (messageError) {
          // Error saving initial message
        }
      }

      // 초기 이력서 데이터 생성
      const { data: resume, error: resumeError } = await supabase
        .from('resumes')
        .insert([{
          session_id: session.id,
          user_id: user.id,
          name: userName || '',
          key_strengths: [],
          experiences: [],
          education: [],
          skills: [],
          certificates: []
        }])
        .select()
        .single();

      if (resumeError) {
        // Error creating resume
      }

      // 메시지 상태 업데이트 (배열의 각 메시지를 개별 메시지로)
      setMessages(firstAiMessages.map((content, index) => ({
        id: `ai-initial-greeting-${index}`,
        sender: 'ai',
        content: content,
        timestamp: new Date(),
      })));

      // 이력서 데이터 초기화
      setExtractedResumeData({
        name: userName || '',
        email: '',
        phone: '',
        address: '',
        github: '',
        title: '',
        summary: '',
        keyStrengths: [],
        experiences: [],
        education: [],
        skills: [],
        certificates: [],
      });

    } catch (error) {
      console.error('Error in startNewSession:', error);
      
      // 더 자세한 에러 정보 출력
      if (error.code) {
        console.error('에러 코드:', error.code);
      }
      if (error.message) {
        console.error('에러 메시지:', error.message);
      }
      if (error.details) {
        console.error('에러 세부사항:', error.details);
      }
      
      // 사용자에게 더 구체적인 에러 메시지 표시
      let userMessage = '컨설팅 세션을 시작하는 중 오류가 발생했습니다.';
      if (error.code === '23503') {
        userMessage = '사용자 프로필 설정 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.code === '23505') {
        userMessage = '이미 진행 중인 세션이 있습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.message && error.message.includes('409')) {
        userMessage = '세션 생성 중 충돌이 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      alert(userMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 메시지 추가 (개선됨: 저장된 메시지 ID 반환)
  const addMessage = async (message) => {
    if (!currentSession) {
      console.error('No active session');
      return null;
    }

    try {
      // 데이터베이스에 저장하고 실제 ID 받기
      const { data: savedMessage, error } = await supabase
        .from('chat_messages')
        .insert([{
          session_id: currentSession.id,
          sender: message.sender,
          content: message.content,
          message_type: message.message_type || 'text',
          is_typing: message.isTyping || false
        }])
        .select('id')
        .single();

      if (error) {
        console.error('Error saving message:', error);
        return null;
      }

      // DB에서 생성된 ID를 가진 메시지 객체 생성
      const finalMessage = {
        ...message,
        id: savedMessage.id,
        timestamp: new Date(),
      };

      // 로컬 상태 업데이트 (한 번만)
      setMessages(prev => [...prev, finalMessage]);

      // 세션 활동 시간 업데이트 (비동기)
      supabase
        .from('consulting_sessions')
        .update({ last_activity_at: new Date().toISOString() })
        .eq('id', currentSession.id)
        .then(({ error: updateError }) => {
          if (updateError) {
            console.error('Error updating session activity:', updateError);
          }
        });

      // 🧹 로그 정리: 메시지 저장 성공 로그 제거 (너무 자주 출력됨)
      return finalMessage;

    } catch (error) {
      console.error('Error in addMessage:', error);
      return null;
    }
  };

  // AI 응답을 기반으로 이력서 데이터 업데이트 (데이터 보존 로직 강화)
  const updateDataFromAI = async (aiResponsePayload) => {
    if (!currentSession) {
      console.error('No active session for data update');
      return;
    }

    try {
      const { updatedExperiences, extractedOtherInfo } = aiResponsePayload;
      
      let lastUpdatedFieldForHighlight = null;
      let updateData = {};
      let hasActualChanges = false;

      // ===== 경력 정보 안전 업데이트 로직 =====
      if (updatedExperiences && Array.isArray(updatedExperiences)) {
        
        // 1. 데이터 무결성 검증
        const isValidExperiences = updatedExperiences.every(exp => {
          return exp && 
                 typeof exp === 'object' && 
                 (exp.position || exp.company || exp.overallDescription || 
                  (exp.projects && exp.projects.length > 0));
        });

        // 2. 빈 배열이 아니고 유효한 데이터인 경우만 업데이트
        if (updatedExperiences.length > 0 && isValidExperiences) {
          const currentExperiences = JSON.stringify(extractedResumeData.experiences);
          const newExperiences = JSON.stringify(updatedExperiences);
          
          if (currentExperiences !== newExperiences) {
  
            setExtractedResumeData(prev => ({
              ...prev,
              experiences: [...updatedExperiences],
            }));
            updateData.experiences = updatedExperiences;
            lastUpdatedFieldForHighlight = 'experiences';
            hasActualChanges = true;
          }
        } else if (updatedExperiences.length === 0) {
          // 3. 빈 배열이 온 경우 - 기존 데이터 보존
          
          // 기존 데이터가 있다면 보존 확인 메시지
          if (extractedResumeData.experiences?.length > 0) {

          }
          // 빈 배열은 업데이트하지 않음으로써 기존 데이터 보존
        } else {
          // 4. 무효한 데이터인 경우
          console.warn('⚠️ 무효한 경력 데이터 감지 - 업데이트 건너뜀');
          console.warn('무효한 데이터 샘플:', updatedExperiences.slice(0, 2));
        }
      }

      // ===== 기타 정보 안전 업데이트 로직 =====
      if (extractedOtherInfo) {
        const updatesToApply = Array.isArray(extractedOtherInfo) ? extractedOtherInfo : [extractedOtherInfo];
        
        const allowedFields = [
          'name', 'email', 'phone', 'address', 'github', 
          'title', 'summary', 'keyStrengths', 'skills', 
          'education', 'certificates'
        ];
        
        updatesToApply.forEach(update => {
          if (update && update.field && update.data !== undefined) {
            // 1. 허용되지 않은 필드 스킵
            
            // 2. 배열 필드에 대한 빈 배열 보조 로직
            const arrayFields = ['keyStrengths', 'skills', 'education', 'certificates'];
            if (arrayFields.includes(update.field) && Array.isArray(update.data)) {
              // 빈 배열이고 기존에 데이터가 있다면 보존
              if (update.data.length === 0) {
                const currentData = extractedResumeData[update.field];
                if (currentData && currentData.length > 0) {
                  console.warn(`⚠️ AI가 빈 ${update.field} 배열을 반환 - 기존 데이터 보존`);
                  return; // 빈 배열은 업데이트하지 않음
                }
              }
              
              // 데이터 유효성 검증
              const isValidArray = validateArrayField(update.field, update.data);
              if (!isValidArray) {
                console.warn(`⚠️ 무효한 ${update.field} 데이터 스킵:`, update.data.slice(0, 2));
                return;
              }
            }
            
            // 3. 기존 데이터와 비교
            const currentValue = extractedResumeData[update.field];
            const newValue = update.data;
            
            const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(newValue);
            
            if (hasChanged) {
              
              setExtractedResumeData(prev => ({
                ...prev,
                [update.field]: newValue,
              }));
              
              const dbFieldName = update.field === 'keyStrengths' ? 'key_strengths' : update.field;
              updateData[dbFieldName] = newValue;
              
              lastUpdatedFieldForHighlight = update.field;
              hasActualChanges = true;
            }
          }
        });
      }

      // 실제 변경사항이 있을 때만 데이터베이스 업데이트 및 하이라이트 효과 적용
      if (hasActualChanges && Object.keys(updateData).length > 0) {
        updateData.currently_updating_field = lastUpdatedFieldForHighlight;
        
        const { error } = await supabase
          .from('resumes')
          .update(updateData)
          .eq('session_id', currentSession.id)
          .eq('is_active', true);

        if (error) {
          console.error('Error updating resume data:', error);
        } else {
          
          // 🔧 데이터 업데이트 시 자동 진행률 계산
          await calculateAndUpdateProgress();
        }

        // 실제 변경이 있었을 때만 하이라이트 효과 설정
        if (lastUpdatedFieldForHighlight) {
          // 🧹 로그 정리: 하이라이트 로그 제거
          setCurrentlyUpdatingField(lastUpdatedFieldForHighlight);
          
          // 2초 후 하이라이트 효과 제거
          setTimeout(() => {
            setCurrentlyUpdatingField(null);
          }, 2000);
        }
      }

    } catch (error) {
      console.error('❌ updateDataFromAI 오류:', error);
      
      // 오류 발생 시 데이터 무결성 확인 메시지
      console.warn('🛡️ 데이터 업데이트 중 오류 발생 - 기존 데이터 보존됨');
    }
  };
  
  // ===== 배열 필드 유효성 검증 함수 =====
  const validateArrayField = (fieldName, data) => {
    if (!Array.isArray(data)) return false;
    
    switch (fieldName) {
      case 'keyStrengths':
      case 'skills':
        return data.every(item => typeof item === 'string' && item.trim().length > 0);
      
      case 'education':
        return data.every(edu => 
          edu && typeof edu === 'object' && (edu.degree || edu.school)
        );
      
      case 'certificates':
        return data.every(cert => 
          cert && typeof cert === 'object' && cert.name
        );
      
      case 'experiences':
        return data.every(exp => 
          exp && typeof exp === 'object' && (exp.position || exp.company)
        );
      
      default:
        return true;
    }
  };

  // 세션 진행률 업데이트
  const updateSessionProgress = async (step, percentage) => {
    if (!currentSession) {
      console.log('⚠️ No current session for progress update');
      return;
    }

    try {
      // 🧹 로그 정리: 진행률 업데이트 로그 간소화
      
      // 진행률이 100%에 도달했는지 확인
      const isCompleted = percentage >= 100;
      const updateData = {
        current_step: step, 
        progress_percentage: percentage,
        last_activity_at: new Date().toISOString()
      };
      
      // 진행률 100% 달성 시 자동 완료 처리
      if (isCompleted && currentSession.status !== 'completed') {
        updateData.status = 'completed';
        updateData.completed_at = new Date().toISOString();
      }
      
      const { error } = await supabase
        .from('consulting_sessions')
        .update(updateData)
        .eq('id', currentSession.id);

      if (error) {
        console.error('❌ Error updating session progress:', error);
      } else {
        
        setCurrentSession(prev => ({
          ...prev,
          current_step: step,
          progress_percentage: percentage,
          ...(isCompleted && { status: 'completed', completed_at: new Date().toISOString() })
        }));
      }
    } catch (error) {
      console.error('❌ Error in updateSessionProgress:', error);
    }
  };

  // 세션 완료
  const completeSession = async () => {
    if (!currentSession) return;

    try {
      const { error } = await supabase
        .from('consulting_sessions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString(),
          progress_percentage: 100
        })
        .eq('id', currentSession.id);

      if (error) {
        console.error('Error completing session:', error);
      } else {
        setCurrentSession(prev => ({
          ...prev,
          status: 'completed',
          progress_percentage: 100
        }));
      }
    } catch (error) {
      console.error('Error in completeSession:', error);
    }
  };

  // AI 로그 저장
  const logAIInteraction = async (logType, userMessage, aiResponse, processingTime, currentStep, apiUsed, tokenCount, errorMessage) => {
    if (!currentSession) return;

    try {
      const { error } = await supabase
        .from('ai_logs')
        .insert([{
          session_id: currentSession.id,
          log_type: logType,
          user_message: userMessage,
          ai_response: aiResponse,
          processing_time_ms: processingTime,
          current_step: currentStep,
          api_used: apiUsed,
          token_count: tokenCount,
          error_message: errorMessage
        }]);

      if (error) {
        console.error('Error logging AI interaction:', error);
      }
    } catch (error) {
      console.error('Error in logAIInteraction:', error);
    }
  };

  // 최신 세션 찾기 (온보딩용)
  const findLatestSession = async () => {
    if (!user) {
      return null;
    }

    try {
      // 사용자의 모든 세션을 생성일 기준 내림차순으로 조회 (삭제되지 않은 것만)
      const { data: sessions, error } = await supabase
        .from('consulting_sessions')
        .select('*')
        .eq('user_id', user.id)
        .is('deleted_at', null)  // 삭제되지 않은 세션만 조회
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('❌ 세션 조회 오류:', error);
        return null;
      }

      if (sessions && sessions.length > 0) {
        return sessions[0];
      }

      return null;
    } catch (error) {
      console.error('❌ findLatestSession 오류:', error);
      return null;
    }
  };

  // 온보딩용 세션 준비 (기존 세션 로드 또는 새 세션 생성)
  const prepareSessionForOnboarding = async (userName = '') => {
    if (!user) {
      throw new Error('사용자가 로그인하지 않음');
    }

    try {
      setLoading(true);

      // 1. 최신 세션 확인
      const latestSession = await findLatestSession();
      
      if (latestSession) {
        // 기존 세션 로드
        await loadExistingSession(latestSession.id);
        
        return {
          session: latestSession,
          isNewSession: false
        };
      } else {
        // 새 세션 생성
        await startNewSession(userName);
        
        return {
          session: currentSession,
          isNewSession: true
        };
      }
    } catch (error) {
      console.error('❌ prepareSessionForOnboarding 실패:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSession = useCallback(async (sessionId) => {
    if (!user) {
      throw new Error('User not logged in');
    }

    try {
      setLoading(true);

      // 🔥 중요: 기존 상태 완전 초기화 (세션 격리의 핵심)
      setMessages([]);
      setExtractedResumeData({
        name: '',
        email: '',
        phone: '',
        address: '',
        github: '',
        title: '',
        summary: '',
        keyStrengths: [],
        experiences: [],
        education: [],
        skills: [],
        certificates: [],
      });
      setCurrentSession(null);
      setCurrentlyUpdatingField(null);

      // Gemini 채팅 세션도 리셋 (loadExistingSession에서는 파라미터로 받은 sessionId 사용)
      const { resetChatSession } = await import('../utils/geminiAPI_SDK');
      // loadExistingSession은 sessionId 파라미터를 받음
      if (sessionId) {
        resetChatSession(sessionId);
      }

      // 세션 정보 조회
      const { data: session, error: sessionError } = await supabase
        .from('consulting_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .is('deleted_at', null)  // 삭제되지 않은 세션만 조회
        .single();

      if (sessionError) {
        console.error('❌ 세션 조회 실패:', sessionError);
        throw new Error(`세션을 찾을 수 없습니다: ${sessionError.message}`);
      }

      if (!session) {
        throw new Error('세션이 존재하지 않거나 접근 권한이 없습니다.');
      }

      //console.log('✅ 세션 정보 조회 완료:', session);

      // 기존 활성 세션들을 완료 상태로 변경 (현재 로드하는 세션 제외)
      await supabase
        .from('consulting_sessions')
        .update({ status: 'completed' })
        .eq('user_id', user.id)
        .eq('status', 'active')
        .neq('id', sessionId);

      // 현재 세션을 활성 상태로 변경
      const { error: updateError } = await supabase
        .from('consulting_sessions')
        .update({ 
          status: 'active',
          last_activity_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (updateError) {
        console.error('❌ 세션 상태 업데이트 실패:', updateError);
      }

      // 세션 상태 업데이트
      setCurrentSession({
        ...session,
        status: 'active'
      });

      // 세션의 메시지들 로드
      await loadSessionMessages(sessionId);

      // 세션의 이력서 데이터 로드
      await loadSessionResumeData(sessionId);
      
      // Gemini 채팅 세션 복원 (기존 대화 기록과 함께)
      const { initializeChatSession } = await import('../utils/geminiAPI_SDK');
      
      // 메시지 로드가 완료된 후 대화 기록 가져오기
      const { data: chatMessages, error: msgError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      
      if (!msgError && chatMessages && chatMessages.length > 0) {
        // DB 대화 기록을 Gemini에 전달할 형식으로 변환
        const conversationHistory = [];
        
        for (let i = 0; i < chatMessages.length; i++) {
          const msg = chatMessages[i];
          if (msg.sender === 'user') {
            // 다음 AI 응답 찾기
            const nextAiMsg = chatMessages.find(
              (m, idx) => idx > i && m.sender === 'ai'
            );
            
            if (nextAiMsg) {
              conversationHistory.push({
                userMessage: msg.content,
                aiResponse: nextAiMsg.content
              });
            }
          }
        }
        
        // 이력서 데이터와 대화 기록으로 Gemini 세션 초기화
        const { data: resumes } = await supabase
          .from('resumes')
          .select('*')
          .eq('session_id', sessionId)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);
        
        const resumeData = resumes?.[0] ? {
          name: resumes[0].name || '',
          email: resumes[0].email || '',
          phone: resumes[0].phone || '',
          address: resumes[0].address || '',
          github: resumes[0].github || '',
          title: resumes[0].title || '',
          summary: resumes[0].summary || '',
          keyStrengths: resumes[0].key_strengths || [],
          experiences: resumes[0].experiences || [],
          education: resumes[0].education || [],
          skills: resumes[0].skills || [],
          certificates: resumes[0].certificates || [],
        } : extractedResumeData;
        
        await initializeChatSession(
          resumeData,
          user.id,
          sessionId,
          conversationHistory
        );
      }

    } catch (error) {
      console.error('❌ loadExistingSession 실패:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]); // useCallback 의존성

  // 이력서 수동 업데이트 (편집 페이지용)
  const updateResumeData = async (updates) => {
    if (!currentSession) {
      console.error('No active session for manual update');
      return;
    }

    try {
      // 로컬 상태 업데이트
      setExtractedResumeData(prev => ({
        ...prev,
        ...updates
      }));

      // 필드명 변환 (camelCase -> snake_case)
      const dbUpdates = {};
      Object.keys(updates).forEach(key => {
        const dbKey = key === 'keyStrengths' ? 'key_strengths' : key;
        dbUpdates[dbKey] = updates[key];
      });

      // 데이터베이스 업데이트
      const { error } = await supabase
        .from('resumes')
        .update(dbUpdates)
        .eq('session_id', currentSession.id)
        .eq('is_active', true);

      if (error) {
        console.error('Error updating resume data manually:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateResumeData:', error);
      return { success: false, error: error.message };
    }
  };

  // 📊 실제 데이터를 기반으로 진행률 자동 계산 및 업데이트 (progressSync.js 로직 사용)
  const calculateAndUpdateProgress = async () => {
    if (!currentSession) return;

    try {
      // progressSync.js의 통합된 계산 로직 사용
      const calculatedProgress = calculateResumeProgress(extractedResumeData);
      
      // 현재 DB의 진행률과 다르면 업데이트
      if (calculatedProgress !== currentSession.progress_percentage) {
        // 단계 계산 (진행률에 따른)
        let calculatedStep = currentSession.current_step || "1";
        if (calculatedProgress >= 90) calculatedStep = "7";
        else if (calculatedProgress >= 70) calculatedStep = "6";
        else if (calculatedProgress >= 50) calculatedStep = "5";
        else if (calculatedProgress >= 30) calculatedStep = "4";
        else if (calculatedProgress >= 20) calculatedStep = "3";
        else if (calculatedProgress >= 10) calculatedStep = "2";
        
        // 세션 진행률 업데이트 (기존 함수 활용)
        await updateSessionProgress(calculatedStep, calculatedProgress);
      }
    } catch (error) {
      console.error('❌ 진행률 자동 계산 중 오류:', error);
    }
  };

  // ===== 개선된 데이터 무결성 감시 시스템 =====
  useEffect(() => {
    if (!currentSession?.id) return;

    const integrityCheckInterval = setInterval(async () => {
      // 중요 데이터가 비어있는지 확인
      const hasEmptyExperiences = extractedResumeData.experiences?.length === 0;
      const hasEmptyKeyStrengths = extractedResumeData.keyStrengths?.length === 0;
      const hasEmptyName = !extractedResumeData.name?.trim();
      const hasEmptySkills = extractedResumeData.skills?.length === 0;

      // 4개 중요 필드가 모두 비어있다면 데이터 손실 가능성
      const criticalDataLoss = hasEmptyExperiences && hasEmptyKeyStrengths && hasEmptyName && hasEmptySkills;
      
      if (criticalDataLoss) {
        console.warn('🚨 데이터 손실 감지');
        
        try {
          // 데이터베이스에서 현재 세션의 이력서 데이터 확인
          const { data: dbResumes, error: dbError } = await supabase
            .from('resumes')
            .select('*')
            .eq('session_id', currentSession.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(1);

          if (!dbError && dbResumes && dbResumes.length > 0) {
            const dbResume = dbResumes[0];
            
            // 데이터베이스에 실제 데이터가 있는지 확인
            const dbHasData = (
              (dbResume.experiences && dbResume.experiences.length > 0) ||
              (dbResume.key_strengths && dbResume.key_strengths.length > 0) ||
              (dbResume.name && dbResume.name.trim()) ||
              (dbResume.skills && dbResume.skills.length > 0)
            );

            if (dbHasData) {
              // 데이터베이스에서 복구
              setExtractedResumeData({
                name: dbResume.name || '',
                email: dbResume.email || '',
                phone: dbResume.phone || '',
                address: dbResume.address || '',
                github: dbResume.github || '',
                title: dbResume.title || '',
                summary: dbResume.summary || '',
                keyStrengths: dbResume.key_strengths || [],
                experiences: dbResume.experiences || [],
                education: dbResume.education || [],
                skills: dbResume.skills || [],
                certificates: dbResume.certificates || [],
              });
              
              return;
            } else {
              // DB에도 데이터 없음
            }
          } else {
            // DB 조회 실패
          }

          // 데이터베이스에도 데이터가 없다면 무시
          // 데이터 손실 감지 여부 확인
          const hadPreviousData = (
            messages.length > 3 || 
            currentSession.progress_percentage > 10
          );
          
          // 심각한 데이터 손실인 경우만 최소한의 로그
          if (hadPreviousData) {
            console.error('🚨 데이터 손실 감지');
          }
          
        } catch (error) {
          // 복구 시도 중 오류 발생
        }
      }
    }, 30000); // 30초마다 체크

    return () => clearInterval(integrityCheckInterval);
  }, [extractedResumeData.experiences, extractedResumeData.keyStrengths, extractedResumeData.name, extractedResumeData.skills, currentSession?.id, messages.length, currentSession?.progress_percentage]);

  const value = {
    currentSession,
    messages,
    extractedResumeData,
    currentlyUpdatingField,
    loading,
    startNewSession,
    addMessage,
    updateDataFromAI,
    updateSessionProgress,
    completeSession,
    logAIInteraction,
    updateResumeData,
    loadActiveSession,
    loadExistingSession,
    calculateAndUpdateProgress,
    findLatestSession,
    prepareSessionForOnboarding,
    updateSessionProgressFromResumeData,
  };

  return (
    <ConsultingContext.Provider value={value}>
      {children}
    </ConsultingContext.Provider>
  );
};